/**
 * AI 分析详情（详情页左列第 2 卡片）
 *
 * UI 对齐 web后台.html：
 *  - 两列布局：左 识别对象（每条卡片含 name/value）；右 规则检查（绿/红 卡片含原因）
 *  - 底部参考标准 banner（蓝底）
 *
 * 接口数据来源（2026-05-20 起 result 字段从 URL 字符串改为结构化对象）：
 *  - 取最新一轮 AI 分析（ai_analysis_list 末尾元素）作为主展示
 *  - result.objects → 识别对象列表
 *  - result.rules   → 规则命中结果(优先);无 rules 时 fallback knowledge_rules.rule_details
 *  - result.all_compliant → 整体合格判定(同时兼容 status === 0)
 *  - knowledge_rules.watermark_rule 作为参考标准
 */
import React, { useMemo } from 'react'
import { BookOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import {
  AiAnalysisItem,
  AiAnalysisResultData,
  AiAnalysisResultObject,
  AiAnalysisResultRule,
  CheckDetailResponse,
  KnowledgeRulesBlock
} from '../api/check'
import { AI_STATUS_COLOR, AI_STATUS_LABEL } from '../constants/check'

interface AiAnalysisDetailProps {
  aiList: AiAnalysisItem[]
  knowledgeRules: CheckDetailResponse['knowledge_rules']
}

/**
 * result 字段类型保护:新接口返回对象,老数据可能仍是 URL 字符串。
 * 字符串/空值前端没法直接渲染(要 fetch URL),按"无数据"处理。
 */
function asResultData(
  result: AiAnalysisItem['result'] | undefined
): AiAnalysisResultData | null {
  if (result && typeof result === 'object') return result
  return null
}

const AiAnalysisDetail: React.FC<AiAnalysisDetailProps> = ({
  aiList,
  knowledgeRules
}) => {
  const latest: AiAnalysisItem | null =
    aiList && aiList.length > 0 ? aiList[aiList.length - 1] : null

  const resultData = useMemo(() => asResultData(latest?.result), [latest])

  const objects: AiAnalysisResultObject[] = resultData?.objects || []

  const aiRules: AiAnalysisResultRule[] = resultData?.rules || []
  const knowledgeRuleList: KnowledgeRulesBlock['rule_details'] =
    knowledgeRules?.rule_details || []

  // 整体合格判定:优先用 result.all_compliant;无 result 时退回 status === 0
  const overallPass =
    typeof resultData?.all_compliant === 'boolean'
      ? resultData.all_compliant
      : latest?.status === 0

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 20
        }}
      >
        {/* 左：识别对象 */}
        <div>
          <div
            style={{
              fontSize: 12,
              color: '#9CA3AF',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <span>识别对象</span>
            {latest && (
              <span
                style={{
                  fontSize: 11,
                  padding: '0 8px',
                  height: 18,
                  lineHeight: '18px',
                  borderRadius: 9,
                  color: AI_STATUS_COLOR[latest.status],
                  background: `${AI_STATUS_COLOR[latest.status]}15`
                }}
              >
                第 {latest.round} 轮 · {AI_STATUS_LABEL[latest.status]}
              </span>
            )}
          </div>
          {objects.length === 0 ? (
            <div
              style={{
                background: '#F9FAFB',
                borderRadius: 8,
                padding: '24px 12px',
                textAlign: 'center',
                color: '#9CA3AF',
                fontSize: 12
              }}
            >
              暂无 AI 识别结果
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {objects.map((obj, i) => (
                <div
                  key={i}
                  style={{
                    background: '#F9FAFB',
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 12,
                    color: '#1F2937',
                    lineHeight: 1.6,
                    wordBreak: 'break-word'
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>
                    {obj.name || '-'}
                  </div>
                  <div style={{ color: '#4B5563' }}>{obj.value || '-'}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 右：规则检查 */}
        <div>
          <div
            style={{
              fontSize: 12,
              color: '#9CA3AF',
              marginBottom: 8
            }}
          >
            规则检查
          </div>
          {aiRules.length === 0 && knowledgeRuleList.length === 0 ? (
            <div
              style={{
                background: '#F9FAFB',
                borderRadius: 8,
                padding: '24px 12px',
                textAlign: 'center',
                color: '#9CA3AF',
                fontSize: 12
              }}
            >
              暂无规则
            </div>
          ) : aiRules.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {aiRules.map((rule, i) => {
                const ok = rule.is_compliant
                return (
                  <div
                    key={i}
                    style={{
                      background: ok ? '#F0FAF5' : '#FEF4F4',
                      border: `1px solid ${ok ? '#CCEBDA' : '#F8D6D6'}`,
                      borderRadius: 8,
                      padding: '10px 12px'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 8
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'flex-start',
                          gap: 6,
                          fontSize: 12,
                          color: '#1F2937',
                          fontWeight: 500,
                          lineHeight: 1.6
                        }}
                      >
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: 999,
                            background: ok ? '#10A66B' : '#E84C4C',
                            display: 'grid',
                            placeItems: 'center',
                            color: '#fff',
                            flexShrink: 0,
                            marginTop: 2
                          }}
                        >
                          {ok ? (
                            <CheckOutlined style={{ fontSize: 9 }} />
                          ) : (
                            <CloseOutlined style={{ fontSize: 9 }} />
                          )}
                        </span>
                        <span style={{ wordBreak: 'break-word' }}>
                          {rule.name}
                        </span>
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: ok ? '#10A66B' : '#E84C4C',
                          fontWeight: 600,
                          flexShrink: 0
                        }}
                      >
                        {ok ? '通过' : '不通过'}
                      </span>
                    </div>
                    {!ok && rule.reason && (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#9CA3AF',
                          marginTop: 6,
                          paddingLeft: 20,
                          lineHeight: 1.6,
                          wordBreak: 'break-word'
                        }}
                      >
                        {rule.reason}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {knowledgeRuleList.map((rule) => {
                // fallback: 后端没返回 result.rules 时,只能按整体合格状态推断
                const ok = overallPass
                return (
                  <div
                    key={rule.rule_detail_id}
                    style={{
                      background: ok ? '#F0FAF5' : '#FEF4F4',
                      border: `1px solid ${ok ? '#CCEBDA' : '#F8D6D6'}`,
                      borderRadius: 8,
                      padding: '10px 12px'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 12,
                          color: '#1F2937',
                          fontWeight: 500
                        }}
                      >
                        <span
                          style={{
                            width: 14,
                            height: 14,
                            borderRadius: 999,
                            background: ok ? '#10A66B' : '#E84C4C',
                            display: 'grid',
                            placeItems: 'center',
                            color: '#fff'
                          }}
                        >
                          {ok ? (
                            <CheckOutlined style={{ fontSize: 9 }} />
                          ) : (
                            <CloseOutlined style={{ fontSize: 9 }} />
                          )}
                        </span>
                        {rule.obj}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: ok ? '#10A66B' : '#E84C4C',
                          fontWeight: 600
                        }}
                      >
                        {ok ? '通过' : '不通过'}
                      </span>
                    </div>
                    {(rule.range || rule.rule || rule.spec) && (
                      <div
                        style={{
                          fontSize: 11,
                          color: '#9CA3AF',
                          marginTop: 6,
                          paddingLeft: 20,
                          lineHeight: 1.6
                        }}
                      >
                        {[rule.spec, rule.range, rule.rule]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* 参考标准 banner */}
      {knowledgeRules?.watermark_rule && (
        <div
          style={{
            marginTop: 16,
            background: '#EFF6FF',
            border: '1px solid #DBEAFE',
            borderRadius: 8,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}
        >
          <BookOutlined style={{ color: '#3B7DEB' }} />
          <span style={{ fontSize: 12, color: '#1E40AF', fontWeight: 600 }}>
            参考标准
          </span>
          <span
            style={{
              width: 1,
              height: 12,
              background: '#BFDBFE'
            }}
          />
          <span style={{ fontSize: 12, color: '#1F2937' }}>
            {knowledgeRules.watermark_rule}
          </span>
        </div>
      )}
    </div>
  )
}

export default AiAnalysisDetail
